import currency from "currency.js";
import moment from "moment";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  InstrumentType,
  InstrumentTypeResponse,
  OptionTypeResponse,
} from "../../../codegen-api";
import { getDefaultInstrumentType } from "../../../config/features";
import { nanosToSeconds } from "../../../utils/date";
import MarketIcon from "../../PortfolioSettings/Table/MarketIcon";
import { MarketHeaderContainer, MarketHeaderContent, PriceInfo, Separator } from "./style";
import { COLORS } from "../../../constants/design/colors";
import { getAssetFromSymbol } from "../../../utils/instruments";
import { MarketInstrumentContext } from "../../../contexts/MarketInstrumentContext";
import { formatKnownPrice } from "../../../utils/unknownNumbers";
import { getDisplayInstrumentName } from "../../../utils/displayName";

interface IMarketCellProps {
  instrumentName?: string;
  /**
   * The row's vertical. Selects whose price step formats the price: a spot
   * and a perp market of one asset have different steps. Absent, the build's
   * default vertical -- not "perpetual", which a spot-only build has none of.
   */
  instrumentType?: InstrumentTypeResponse | string;
  optionType?: OptionTypeResponse;
  // Nanos
  expiry?: string;
  strike?: string;

  /** Absent, null or 0 renders "-". */
  price?: string | null;
  // 0 to 1
  priceIncreasePercent?: number;
}

function MarketHeader({
  instrumentName,
  instrumentType,
  optionType,
  expiry,
  strike,
  price,
  priceIncreasePercent
}: IMarketCellProps) {
  const { t } = useTranslation("app", { keyPrefix: "shared.ViewDetailsModal.MarketHeader" });
  const { getMarketPrecision } = useContext(MarketInstrumentContext);
  const isOption = useMemo(
    () => optionType !== undefined && expiry !== undefined && strike !== undefined,
    [expiry, optionType, strike]
  );

  const content = useMemo(() => {
    if (isOption) {
      return (
        <MarketHeaderContent>
          {optionType}
          <Separator />
          {moment.unix(nanosToSeconds(expiry!)).format("DD MMM YY")}
          <Separator />
          {currency(strike!, { precision: 0 }).format()}
        </MarketHeaderContent>
      );
    }
    return (
      <MarketHeaderContent>
        {getDisplayInstrumentName(instrumentName) || instrumentName}
      </MarketHeaderContent>
    );
  }, [expiry, instrumentName, isOption, optionType, strike]);

  const precisionType = isOption
    ? InstrumentType.Option
    : (instrumentType as InstrumentTypeResponse | undefined) ??
      getDefaultInstrumentType();

  const subContent = useMemo(() => {
    if (isOption) {
      return (
        <PriceInfo>
          <span>{t("mark_price")}</span>
          <span>{formatKnownPrice(price)}</span>
        </PriceInfo>
      );
    }

    return (
      <PriceInfo>
        <span>{formatKnownPrice(price, getMarketPrecision(getAssetFromSymbol(instrumentName!), precisionType).price_precision)}</span>
        {
          priceIncreasePercent && (
            <span style={{
              color: priceIncreasePercent >= 0
                ? COLORS.up.one
                : COLORS.down.one
            }}
            >
              {(priceIncreasePercent * 100).toFixed(2)}%
            </span>
          )
        }
      </PriceInfo>
    );
  }, [isOption, price, getMarketPrecision, instrumentName, precisionType, priceIncreasePercent, t]);

  return (
    <MarketHeaderContainer>
      <MarketIcon instrumentName={instrumentName} />
      <div>
        {content}
        {subContent}
      </div>
    </MarketHeaderContainer>
  );
}

export default MarketHeader;
